import axios from 'axios'
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react'

import { useCardContext } from './CardContext'

import type {
  PointsRedemptionResponse,
  PointsRedemptionProductVoucherResponse,
  PointsRedemptionPurchaseVoucherResponse,
} from '../types/PointsRedemption'
import type { PropsWithChildren } from 'react'

interface PointsRedemptionContextProps {
  pointsRedemption?: PointsRedemption
  getPointsRedemption?: () => Promise<void>
  pointsRedemptionVendor?: PointsRedemptionByVendor
  getPointsRedemptionVendor?: (vendor: string) => Promise<void>
  productDetails?: ProductDetailsVoucher
  getProductDetails?: (sku: string) => Promise<void>
  purchaseVoucher?: (sku: string, vendorId: number) => Promise<boolean>
}

export type ProductDetailsVoucher =
  QueryStatus<PointsRedemptionProductVoucherResponse>

export type PointsRedemption = QueryStatus<PointsRedemptionResponse>
export type PointsRedemptionByVendor = QueryStatus<PointsRedemptionResponse>

interface QueryStatus<T> {
  loading: boolean
  error: boolean
  data: T | undefined
}

const PointsRedemptionContext = createContext<
  PointsRedemptionContextProps | undefined
>(undefined)

export const PointsRedemptionProvider = ({
  children,
}: PropsWithChildren<PointsRedemptionContextProps>) => {
  const { setBalance, getBalance } = useCardContext()

  const [pointsRedemption, setPointsRedemption] = useState<PointsRedemption>({
    loading: false,
    error: false,
    data: undefined,
  })

  const [pointsRedemptionVendor, setPointsRedemptionVendor] =
    useState<PointsRedemptionByVendor>({
      loading: false,
      error: false,
      data: undefined,
    })

  const [productDetails, setProductDetails] = useState<ProductDetailsVoucher>({
    loading: false,
    error: false,
    data: undefined,
  })

  const getPointsRedemption = useCallback(async () => {
    setPointsRedemption({ loading: true, error: false, data: undefined })

    try {
      const { data } = await axios.get<PointsRedemptionResponse>(
        '/api/loyalty/marketplaceSearch',
        { params: { platform: 'web', random: Math.random() } }
      )

      setPointsRedemption({ loading: false, error: false, data })
    } catch {
      setPointsRedemption({ loading: false, error: true, data: undefined })
    }
  }, [])

  const getPointsRedemptionVendor = useCallback(async (vendor: string) => {
    setPointsRedemptionVendor({ loading: true, error: false, data: undefined })

    try {
      const { data } = await axios.get<PointsRedemptionResponse>(
        '/api/loyalty/marketplaceSearch',
        { params: { platform: 'web', vendor, random: Math.random() } }
      )

      setPointsRedemptionVendor({ loading: false, error: false, data })
    } catch {
      setPointsRedemptionVendor({
        loading: false,
        error: true,
        data: undefined,
      })
    }
  }, [])

  const getProductDetails = useCallback(async (sku: string) => {
    setProductDetails({ loading: true, error: false, data: undefined })

    try {
      if (!sku) {
        throw new Error(`Um erro ocorreu: SKU not found`)
      }

      const { data } = await axios.get<PointsRedemptionProductVoucherResponse>(
        `/api/loyalty/marketplaceProductDetails/${sku}`,
        { params: { platform: 'web', random: Math.random() } }
      )

      setProductDetails({ loading: false, error: false, data })
    } catch {
      setProductDetails({
        loading: false,
        error: true,
        data: undefined,
      })
    }
  }, [])

  const purchaseVoucher = useCallback(async (sku: string, vendorId: number) => {
    try {
      const { data } =
        await axios.post<PointsRedemptionPurchaseVoucherResponse>(
          `/api/loyalty/marketplacePurchaseVoucher`,
          { sku, vendorId },
          { params: { platform: 'web' } }
        )

      if (data.pointsValue !== undefined && setBalance) {
        setBalance({ loading: false, error: false, data: data.pointsValue })
      } else {
        await getBalance?.()
      }

      return true
    } catch {
      return false
    }
  }, [])

  const values = useMemo(
    () => ({
      pointsRedemption,
      getPointsRedemption,
      pointsRedemptionVendor,
      getPointsRedemptionVendor,
      productDetails,
      getProductDetails,
      purchaseVoucher,
    }),
    [
      getPointsRedemption,
      pointsRedemption,
      pointsRedemptionVendor,
      getPointsRedemptionVendor,
      productDetails,
      getProductDetails,
      purchaseVoucher,
    ]
  )

  return (
    <PointsRedemptionContext.Provider value={values}>
      {children}
    </PointsRedemptionContext.Provider>
  )
}

export const usePointsRedemptionContext = () => {
  const context = useContext(PointsRedemptionContext)

  if (!context) {
    throw new Error(
      'usePointsRedemptionContext must be used within a PointsRedemptionContext'
    )
  }

  return context
}
